import React, { useRef, useState } from "react";
import {
  Row,
  Col,
  InputGroup,
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilter,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import CustomSelect from "./CustomSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./SearchAndFilterBar.scss";
import { ISelectableOption } from "interfaces/ISelectableOption";
import { SearchTypes } from "types/SearchTypes";
import { es } from 'date-fns/locale';

interface SearchAndFilterBarProps {
  searchType: SearchTypes;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchOptions?: ISelectableOption[];
  filterBy: ISelectableOption | null;
  onFilterChange: (newValue: ISelectableOption | null) => void;
  filterOptions: ISelectableOption[];
  isSearchDisabled: boolean;
  /**
   * Defined tooltip if default is not required
   */
  customTooltip?: string;
}

const SearchAndFilterBar: React.FC<SearchAndFilterBarProps> = ({
  searchType,
  searchTerm,
  onSearchChange,
  searchOptions,
  filterBy,
  onFilterChange,
  filterOptions,
  isSearchDisabled,
  customTooltip
}) => {
  const inputRef = useRef(null);
  const handleDateChange = (date: Date | null) => {
    const dateString = date ? date.toISOString().split("T")[0] : "";
    onSearchChange(dateString);
  };
  const [selectedLabel, setSelectedLabel] = useState("Selecciona una opción");
  const renderSearchInput = () => {
    switch (searchType) {
      case "text":
        return (
          <Form.Control
            ref={inputRef}
            type="text"
            placeholder="Buscar..."
            value={searchTerm as string}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ borderRight: "none" }}
            className="search-input"
            disabled={isSearchDisabled}
          />
        );
      case "select":
        return (
          <CustomSelect
            options={searchOptions || []}
            value={
              searchTerm ? { value: searchTerm, label: selectedLabel } : null
            }
            onChange={(newValue) => {
              setSelectedLabel(newValue?.label || "");
              onSearchChange(newValue?.value || "");
            }}
            placeholder="Seleccionar..."
            isFilteredIconVisible={false}
          />
        );
      case "date":
        return (
          <DatePicker
            locale={es}
            selected={searchTerm ? new Date(searchTerm) : null}
            onChange={handleDateChange}
            className="form-control"
            placeholderText="Seleccionar fecha..."
            disabled={isSearchDisabled}
            dateFormat="yyyy-MM-dd"
          />
        );
      case "status":
        return <></>;
      default:
        return null;
    }
  };

  return (
    <>
      <Row className="mb-3">
        <Col xs={6} className="d-flex justify-content-start">
          <InputGroup className="w-75 search-input-group">
            {renderSearchInput()}
            {searchType === "text" && (
              <InputGroup.Text
                style={{ backgroundColor: "white" }}
                className="search-icon"
              >
                <FontAwesomeIcon icon={faSearch} color="gray" />
              </InputGroup.Text>
            )}
          </InputGroup>
        </Col>
        <Col xs={6} className="d-flex justify-content-end">
          <div className="d-flex align-items-center w-75">
            <InputGroup>
              <Button
                className="d-flex align-items-center bg-custom-yellow border-end-0"
                style={{ border: "gray" }}
              >
                <FontAwesomeIcon icon={faFilter} style={{ color: "black" }} />
              </Button>
              <div className="filter-select-container">
                <CustomSelect
                  options={filterOptions}
                  value={filterBy}
                  onChange={onFilterChange}
                  placeholder="Filtrar por..."
                  isFilteredIconVisible={true}
                />
              </div>
            </InputGroup>
          </div>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <span>Ordena las columnas a tu gusto</span>
          <OverlayTrigger
            trigger={["hover", "focus"]}
            placement="right"
            overlay={
              <Tooltip className="custom-tooltip">
                {customTooltip ? customTooltip : "Ordena los registros con la flecha en las columnas"}
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
          </OverlayTrigger>
        </Col>
      </Row>
    </>
  );
};

export default SearchAndFilterBar;
